import React, { useCallback, useMemo, useState } from 'react';
import { isInitialQueryLoading } from '../../../../lib/query/listQuery';
import { Switch, Tooltip, Typography } from 'antd';
import { toast } from '../../../../lib/toast';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import DataTable from '../../../../components/DataTable';
import { ListPage, buildCogActionsColumn } from '../../../../components/list';
import CustomerSegmentFilterBar, { type CustomerSegmentFilterValues } from './CustomerSegmentFilterBar';
import CustomerSegmentViewDrawer from './CustomerSegmentViewDrawer';
import CustomerSegmentFormDrawer from './CustomerSegmentFormDrawer';
import { updateCustomerSegment } from '../api/customerSegments';
import { useCustomerSegments } from '../hooks/useCustomerSegments';
import { useCustomerSegment } from '../hooks/useCustomerSegment';
import { useCreateCustomerSegment } from '../hooks/useCreateCustomerSegment';
import { useUpdateCustomerSegment } from '../hooks/useUpdateCustomerSegment';
import { useDeleteCustomerSegment } from '../hooks/useDeleteCustomerSegment';
import type { CustomerSegmentListItem, CustomerSegmentFormValues } from '../types';

const defaultFilter: CustomerSegmentFilterValues = {
  search: undefined,
};

const CustomerSegmentsPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filter, setFilter] = useState<CustomerSegmentFilterValues>(defaultFilter);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [formSegmentId, setFormSegmentId] = useState<string | null>(null);

  const listParams = useMemo(
    () => ({
      pageNumber: page,
      pageSize,
      search: filter.search || undefined,
    }),
    [page, pageSize, filter.search],
  );

  const { data: paged, isLoading } = useCustomerSegments(listParams);
  const loadingList = isInitialQueryLoading(isLoading, paged);
  const { data: segmentDetail, isLoading: loadingSegment } = useCustomerSegment(
    formMode === 'edit' ? formSegmentId : null,
  );
  const viewSegment = useCustomerSegment(selectedSegmentId);
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteCustomerSegment();

  const toggleMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: CustomerSegmentFormValues }) =>
      updateCustomerSegment(id, values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customer-segments'] });
    },
    onError: (err: Error) => {
      void toast.error(err.message || 'Failed to update segment');
    },
  });

  const createMutation = useCreateCustomerSegment(() => {
    setFormDrawerOpen(false);
  });
  const updateMutation = useUpdateCustomerSegment(formSegmentId, () => {
    setFormDrawerOpen(false);
  });

  const handleAdd = useCallback(() => {
    setFormMode('add');
    setFormSegmentId(null);
    setFormDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((id: string) => {
    setFormMode('edit');
    setFormSegmentId(id);
    setFormDrawerOpen(true);
  }, []);

  const columns: ColumnsType<CustomerSegmentListItem> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'title',
        key: 'title',
        render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        ellipsis: true,
        render: (v: string | null) =>
          v ?? <Typography.Text type="secondary">—</Typography.Text>,
      },
      {
        title: 'Contacts',
        dataIndex: 'contactCount',
        key: 'contactCount',
        width: 100,
        align: 'center',
      },
      {
        title: 'Active',
        key: 'status',
        width: 100,
        render: (_: unknown, record: CustomerSegmentListItem) => (
          <Tooltip
            title={
              record.isActive && record.hasActiveLeads
                ? 'Cannot deactivate: segment has active leads'
                : undefined
            }
          >
            <Switch
              checked={record.isActive}
              disabled={record.isActive && record.hasActiveLeads}
              loading={
                toggleMutation.isPending && toggleMutation.variables?.id === record.id
              }
              onChange={(checked) => {
                toggleMutation.mutate({
                  id: record.id,
                  values: {
                    title: record.title,
                    description: record.description ?? undefined,
                    isActive: checked,
                  },
                });
              }}
            />
          </Tooltip>
        ),
      },
      buildCogActionsColumn<CustomerSegmentListItem>({
        onView: (record) => setSelectedSegmentId(record.id),
        onEdit: (record) => handleEdit(record.id),
        onDelete: (record) => deleteMutation.mutate(record.id),
        editPermission: 'customer-segments.edit',
        deletePermission: 'customer-segments.delete',
        deleteConfirm: () => ({
          title: 'Delete customer segment',
          content: 'Delete this segment? Categories that use it must be updated first.',
        }),
        rowNumber: (_record, index) => (page - 1) * pageSize + index + 1,
      }),
    ],
    [deleteMutation, toggleMutation, handleEdit, page, pageSize],
  );

  const handleEditFromView = () => {
    if (selectedSegmentId) {
      handleEdit(selectedSegmentId);
    }
  };

  const handleSaveForm = (values: CustomerSegmentFormValues) => {
    if (formMode === 'add') {
      createMutation.mutate(values);
    } else if (formSegmentId) {
      updateMutation.mutate(values);
    }
  };

  const handlePaginationChange = useCallback((newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  }, []);

  return (
    <>
      <ListPage
        title="Segments"
        subtitle="Group customers for scoped access, categories, and reporting."
        primaryAction={{
          permission: 'customer-segments.create',
          label: 'Add segment',
          onClick: handleAdd,
        }}
        filters={(
          <CustomerSegmentFilterBar
            value={filter}
            onChange={(next) => {
              setPage(1);
              setFilter(next);
            }}
            onReset={() => {
              setPage(1);
              setFilter(defaultFilter);
            }}
          />
        )}
      >
        <DataTable<CustomerSegmentListItem>
          columns={columns}
          dataSource={paged?.items ?? []}
          loading={loadingList}
          rowKey="id"
          bordered
          striped
          pagination={
            paged
              ? {
                  current: paged.pageNumber,
                  pageSize: paged.pageSize,
                  total: paged.totalCount,
                  onChange: handlePaginationChange,
                }
              : undefined
          }
          emptyText="No customer segments yet. Add one to classify your market."
        />
      </ListPage>

      <CustomerSegmentViewDrawer
        open={selectedSegmentId !== null}
        onClose={() => setSelectedSegmentId(null)}
        customerSegment={viewSegment.data}
        loading={viewSegment.isLoading}
        onEdit={handleEditFromView}
      />

      <CustomerSegmentFormDrawer
        open={formDrawerOpen}
        onClose={() => setFormDrawerOpen(false)}
        mode={formMode}
        customerSegment={segmentDetail}
        loadingCustomerSegment={formMode === 'edit' && formSegmentId !== null ? loadingSegment : false}
        onSave={handleSaveForm}
        saving={createMutation.isPending || updateMutation.isPending}
      />
    </>
  );
};

export default CustomerSegmentsPage;
